import Alert from '@dd3tech/wiggui/Alert'
import Button from '@dd3tech/wiggui/Button'
import Flex from '@dd3tech/wiggui/Flex'
import Modal from '@dd3tech/wiggui/Modal'
import Spinner from '@dd3tech/wiggui/Spinnner'
import Text from '@dd3tech/wiggui/Text'
import { Tab, Tabs } from '@dd3tech/wiggui/Tabs'
import { AdOption } from './AdOption'
import { usePublishOrUnpublishProperty } from '../hooks/usePublishOrUnpublishProperty'
import { ROUTES } from '../../../constants'

export const ModalPublishOrUnpublishProperty = () => {
  const {
    textsByType,
    optionsByTabAndType,
    tabSelected,
    modalState,
    onClose,
    setTabSelected,
    isLoading,
    handleOnClick,
    disabledConfirmButton,
    setDisabledConfirmButton,
    isLoadingPublishOrUnpublish,
    isDisabledByOptionWithoutAd,
    refetchServices
  } = usePublishOrUnpublishProperty()

  if (!modalState?.isOpen) {
    return null
  }

  return (
    <Modal
      className="w-full max-w-[526px] max-h-[99vh]"
      enableBgBlur
      onClose={isLoadingPublishOrUnpublish ? () => ({}) : onClose}
      isOpen={modalState?.isOpen}
    >
      <Modal.Header
        title={textsByType?.title}
        isShowCloseButton
        isShowDivider
      />
      <Modal.Body className="w-full flex gap-6 flex-col items-center justify-center">
        <Tabs className="w-full">
          <Tab
            className="w-full"
            active={tabSelected === 'portals'}
            title="Portales inmobiliarios"
            onClick={() => setTabSelected('portals')}
          />
          <Tab
            className="w-full"
            active={tabSelected === 'associations'}
            title="Asociaciones"
            onClick={() => setTabSelected('associations')}
          />
        </Tabs>
        <Flex
          className="overflow-y-auto h-full pb-5 ui-kit-scrollbar max-h-[50vh]"
          dir="col"
          gap="gap-6"
          items="center"
          justify="start"
        >
          <Flex
            dir="col"
            gap="gap-6"
            items="center"
            justify="start"
            wFull
            className="px-6"
          >
            <Text className="text-body-m text-primary">
              {tabSelected === 'portals' ? (
                <>
                  {textsByType?.portalDescription}
                  {modalState?.type === 'publish' && (
                    <a
                      href={`${ROUTES.MANAGE_PLATFORMS}`}
                      target="_blank"
                      className="font-bold underline ml-1"
                      rel="noreferrer"
                    >
                      vinculaciones.
                    </a>
                  )}
                </>
              ) : (
                textsByType?.associationsDescription
              )}
            </Text>
          </Flex>
          {modalState?.type === 'publish' && tabSelected === 'portals' && (
            <div className="px-6">
              <Alert
                padding="py-2 px-3"
                isOutlined={false}
                className="w-full max-w-[428px] !p-3"
                containerIconClassName="[&>span]:text-primary !items-center !gap-3"
                containerTextClassName="[&>p]:font-semibold [&>p]:!text-body-bold-m flex md:!flex-row md:items-center justify-between w-full"
                customIcon="error"
                buttonOptions={{
                  label: 'Actualizar portales',
                  onClick: refetchServices,
                  size: 'xsmall',
                  className: 'max-w-[140px]',
                  variant: 'secondary'
                }}
                title="¿Vinculaste un nuevo portal? Actualiza tus portales para visualizar los cambios."
                type="neutral"
              />
            </div>
          )}
          <Flex
            dir="col"
            gap="gap-6"
            items="center"
            justify="start"
            wFull
            className="px-6"
          >
            {isLoading && (
              <Flex items="center" justify="center" wFull className="py-2">
                <Spinner color="terciary" />
              </Flex>
            )}
            {!isLoading &&
              optionsByTabAndType?.length > 0 &&
              optionsByTabAndType?.map((option) => {
                const wiggotTitle =
                  modalState?.type === 'publish'
                    ? 'Tus propiedades siempre se publicarán en Wiggot®'
                    : 'Tus propiedades se despublicarán en la bolsa de propiedades de Wiggot®'

                const monopolioTitle =
                  modalState?.type === 'publish'
                    ? 'Tus propiedades siempre se publicarán en Monopolio®'
                    : 'Tus propiedades se despublicarán en la bolsa de propiedades de Monopolio®'

                const generateDelayedText = () => {
                  if (
                    option?.isPendingPublish &&
                    !option?.showDelayedText &&
                    modalState?.type === 'publish'
                  ) {
                    return 'Publicación en proceso...'
                  }

                  if (
                    option.isUpdatingPublish &&
                    !option?.showDelayedText &&
                    modalState.type === 'publish'
                  ) {
                    return 'Actualizando publicación...'
                  }

                  if (!option?.showDelayedText) {
                    return undefined
                  }

                  if (
                    option?.isUpdatingPublish &&
                    modalState?.type === 'publish'
                  ) {
                    return 'Actualización en proceso.\nEl portal puede demorar hasta 48 horas en publicar tus propiedades.'
                  }

                  if (
                    modalState?.type === 'publish' &&
                    option?.isPendingPublish
                  ) {
                    return `Publicación en proceso.\nEl portal puede demorar hasta 48 horas en publicar tus propiedades.`
                  }

                  return modalState?.type === 'publish'
                    ? 'La publicación puede tardar en este portal.'
                    : 'La despublicación puede tardar en este portal.'
                }

                const generateDescription = () => {
                  if (option?.portalId?.toUpperCase() === 'WIGGOT') {
                    return wiggotTitle
                  }
                  if (option?.portalId?.toUpperCase() === 'MONOPOLIO') {
                    return monopolioTitle
                  }

                  return undefined
                }

                return (
                  <AdOption
                    isUpdatingPublish={option?.isUpdatingPublish}
                    publishIsFailed={option?.publishIsFailed}
                    isPendingPublish={option?.isPendingPublish}
                    delayedText={generateDelayedText()}
                    setDisabledConfirmButton={setDisabledConfirmButton}
                    id={option?.associationId?.toString() ?? option?.portalId}
                    tabSelected={tabSelected}
                    key={option?.portalId + option?.associationId}
                    isDisabledAndChecked={option?.checkboxIsCheckedAndDisabled}
                    description={generateDescription()}
                    img={option?.img}
                  />
                )
              })}
            {!isLoading && optionsByTabAndType?.length === 0 && (
              <Text className="text-body-m font-bold text-primary">
                {tabSelected === 'portals'
                  ? `No tienes portales inmobiliarios disponibles para ${textsByType?.type}`
                  : `No tienes asociaciones disponibles para ${textsByType?.type}`}
              </Text>
            )}
          </Flex>
        </Flex>
      </Modal.Body>
      <Modal.Footer
        className="w-full py-[9px] px-6 flex flex-row gap-2 items-center justify-center"
        isShowDivider
      >
        <Button
          variant="terciary"
          fullWidth
          isDisabled={isLoadingPublishOrUnpublish}
          onClick={onClose}
        >
          Cancelar
        </Button>
        <Button
          variant="secondary"
          isLoading={isLoadingPublishOrUnpublish}
          fullWidth
          isDisabled={disabledConfirmButton || isDisabledByOptionWithoutAd}
          onClick={handleOnClick}
        >
          {textsByType?.title}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
